// import components
import { Button } from '@mui/material';
import {
  GridCompact6,
  GridContainer
} from 'components/shared/Grid/Grid.components';
import KmSettings from './KmSettings';
import InvoicingAndTravelSettings from './InvoicingAndTravelSettings';
import { StyledBoxFlexJustifyEnd } from 'components/shared/Box/Box.styles';
import Notes from 'components/employee-portal/notes/Notes';
import { showErrorSnackbar } from 'components/shared/Snackbar';

// import enums
import { DutyLineFlag as DutyLineFlagEnums } from 'components/enums';
import { NotesTypeEnums } from 'components/employee-portal/notes/Notes.types';

// import hooks
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useUpdateDutyLine from '../hooks/useUpdateDutyLine';

// import schema
import { kmSchema } from './DutyOtherSettings.schema';

// import types
import type { DutyOtherSettingsProps } from './DutyOtherSettings.types';
import type { DutyLineFlag } from 'components/types';

// import utils
import dayjs from 'dayjs';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  isRowBlocked,
  isRowNominal,
  preparePeriodsForApi,
  toOptionalNumber
} from '../HoursTable.utils';

const DutyOtherSettings = ({
  row,
  employeeId,
  isEmployeePortal = false
}: DutyOtherSettingsProps) => {
  'use memo';

  const { t } = useTranslation('common');

  const { mutateAsync: updateDutyLine, isLoading: isLoadingKm } =
    useUpdateDutyLine();

  const findFlag = (
    flags: DutyLineFlag[] | undefined,
    candidates: DutyLineFlag[]
  ) => flags?.find((flag) => candidates.includes(flag));

  const defaultValues = {
    finEmpKmManual: row?.finEmpKmManual ?? undefined,
    finEmpTravelExpensesManual: row?.finEmpTravelExpensesManual ?? undefined,
    invoicing: findFlag(row?.flags, [
      DutyLineFlagEnums.INVOICE_BLOCKED,
      DutyLineFlagEnums.INVOICE_UNBLOCK
    ]),

    buh: findFlag(row?.flags, [
      DutyLineFlagEnums.BUH_BLOCKED,
      DutyLineFlagEnums.BUH_DEBLOCK
    ])
  };

  const methods = useForm({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: yupResolver(kmSchema)
  });

  if (!row) return null;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty }
  } = methods;

  const onSubmitKm = async (data: {
    finEmpKmManual?: number;
    finEmpTravelExpensesManual?: number;
    invoicing?: DutyLineFlag;
    buh?: DutyLineFlag;
  }) => {
    if (!row.requestId || !row.organizationalUnit) return;

    const removableFlags = new Set<DutyLineFlagEnums>([
      DutyLineFlagEnums.INVOICE_BLOCKED,
      DutyLineFlagEnums.INVOICE_UNBLOCK,
      DutyLineFlagEnums.BUH_BLOCKED,
      DutyLineFlagEnums.BUH_DEBLOCK
    ]);

    const flags = (() => {
      const uniqueFlags = new Set<DutyLineFlag>(row.flags);
      removableFlags.forEach((flag) => uniqueFlags.delete(flag));
      if (data.invoicing) uniqueFlags.add(data.invoicing);
      if (data.buh) uniqueFlags.add(data.buh);

      return [...uniqueFlags];
    })();

    let periods;
    try {
      periods = preparePeriodsForApi(row.periods);
    } catch (error: any) {
      showErrorSnackbar(t(`hoursTable.${error.message}`));
      return;
    }

    const updatePayload = {
      employeeId,
      requestId: row.requestId,
      isEmployeePortal,
      body: {
        costCenterId: row.organizationalUnit,
        date: dayjs(row.dateKey).toDate().toISOString(),
        employeeId,
        plannedDutyId: row.dutyId,
        waId: row.waId,
        flags,
        periods,
        finEmpKmManual: toOptionalNumber(data.finEmpKmManual),
        finEmpTravelExpensesManual: toOptionalNumber(
          data.finEmpTravelExpensesManual
        )
      }
    };

    await updateDutyLine(updatePayload);
    reset(data);
  };

  return (
    <GridContainer>
      {!isRowNominal(row) && (
        <GridCompact6>
          <FormProvider {...methods}>
            <KmSettings
              control={control}
              isEmployeePortal={isEmployeePortal}
              disabled={isRowBlocked(row)}
            />
            {!isEmployeePortal && (
              <>
                <InvoicingAndTravelSettings
                  control={control}
                  disabled={isRowBlocked(row)}
                />

                <StyledBoxFlexJustifyEnd>
                  <Button
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit(onSubmitKm)}
                    disabled={!isDirty || isLoadingKm || isRowBlocked(row)}
                  >
                    {t('save')}
                  </Button>
                </StyledBoxFlexJustifyEnd>
              </>
            )}
          </FormProvider>
        </GridCompact6>
      )}
      <GridCompact6>
        <Notes
          employeeId={employeeId}
          type={NotesTypeEnums.DUTY_LINE}
          noteData={row.notes ?? []}
          dutyLineId={row.dutyLineId}
          isEmployeePortal={isEmployeePortal}
        />
      </GridCompact6>
    </GridContainer>
  );
};

export default DutyOtherSettings;
